import React from 'react';
import { connect } from 'react-redux';
import YouTube from 'react-youtube';
import { PLAYBACK_STATUSES } from '../../constants';
import AbstractVideoManager, { _mapDispatchToProps, _mapStateToProps } from './AbstractVideoManager';
import logger from '../../../app/logger';
/**
 * Manager of shared video.
 *
 * @returns {void}
 */
class YoutubeVideoManager extends AbstractVideoManager {
    /**
     * Initializes a new YoutubeVideoManager instance.
     *
     * @param {Object} props - This component's props.
     *
     * @returns {void}
     */
    constructor(props) {
        super(props);
        /**
         * Fired on play state toggle.
         *
         * @param {Object} event - The yt player stateChange event.
         *
         * @returns {void}
         */
        this.onPlayerStateChange = (event) => {
            if (event.data === YouTube.PlayerState.PLAYING) {
                this.onPlay();
            }
            else if (event.data === YouTube.PlayerState.PAUSED) {
                this.onPause();
            }
        };
        /**
         * Fired when youtube player is ready.
         *
         * @param {Object} event - The youtube player event.
         *
         * @returns {void}
         */
        this.onPlayerReady = (event) => {
            const { _isOwner } = this.props;
            this.player = event.target;
            this._onVolumeChangeBound = () => {
                this.onVolumeChange();
            };
            this.player.addEventListener('onVolumeChange', this._onVolumeChangeBound);
            if (_isOwner) {
                this.player.addEventListener('onVideoProgress', this.throttledFireUpdateSharedVideoEvent);
            }
            // Try to play, but handle autoplay blocking gracefully
            try {
                this.play();
            }
            catch (error) {
                // Autoplay may be blocked - this is expected in some browsers
                logger.error('YouTube autoplay blocked on player ready - video will remain paused', error);
            }
            // sometimes youtube can get muted state from previous videos played in the browser
            // and as we are disabling controls we want to unmute it
            if (this.isMuted()) {
                this.unMute();
            }
        };
        this.getPlayerOptions = () => {
            const { _isOwner, videoId } = this.props;
            const showControls = _isOwner ? 1 : 0;
            const options = {
                id: 'sharedVideoPlayer',
                opts: {
                    height: '100%',
                    width: '100%',
                    playerVars: {
                        'origin': location.origin,
                        'fs': 0,
                        'autoplay': 0,
                        'controls': showControls,
                        'rel': 0
                    }
                },
                onError: (e) => this.onError(e),
                onReady: this.onPlayerReady,
                onStateChange: this.onPlayerStateChange,
                videoId,
                style: {
                    pointerEvents: _isOwner ? 'auto' : 'none'
                }
            };
            return options;
        };
        this.isPlayerAPILoaded = false;
    }
    /**
     * Indicates the playback state of the video.
     *
     * @returns {string}
     */
    getPlaybackStatus() {
        let status;
        if (!this.player) {
            return;
        }
        const playerState = this.player.getPlayerState();
        if (playerState === YouTube.PlayerState.PLAYING) {
            status = PLAYBACK_STATUSES.PLAYING;
        }
        if (playerState === YouTube.PlayerState.PAUSED) {
            status = PLAYBACK_STATUSES.PAUSED;
        }
        return status;
    }
    /**
     * Indicates whether the video is muted.
     *
     * @returns {boolean}
     */
    isMuted() {
        var _a;
        return (_a = this.player) === null || _a === void 0 ? void 0 : _a.isMuted();
    }
    /**
     * Retrieves current volume.
     *
     * @returns {number}
     */
    getVolume() {
        var _a;
        return (_a = this.player) === null || _a === void 0 ? void 0 : _a.getVolume();
    }
    /**
     * Retrieves current time.
     *
     * @returns {number}
     */
    getTime() {
        var _a;
        return (_a = this.player) === null || _a === void 0 ? void 0 : _a.getCurrentTime();
    }
    /**
     * Seeks video to provided time.
     *
     * @param {number} time - The time to seek to.
     *
     * @returns {void}
     */
    seek(time) {
        var _a;
        return (_a = this.player) === null || _a === void 0 ? void 0 : _a.seekTo(time);
    }
    /**
     * Plays video.
     *
     * @returns {void}
     */
    play() {
        var _a;
        try {
            return (_a = this.player) === null || _a === void 0 ? void 0 : _a.playVideo();
        }
        catch (error) {
            // Handle autoplay blocking gracefully
            logger.error('YouTube playVideo() failed - autoplay may be blocked:', error);
            // Return undefined to indicate failure
            return undefined;
        }
    }
    /**
     * Pauses video.
     *
     * @returns {void}
     */
    pause() {
        var _a;
        return (_a = this.player) === null || _a === void 0 ? void 0 : _a.pauseVideo();
    }
    /**
     * Mutes video.
     *
     * @returns {void}
     */
    mute() {
        var _a;
        return (_a = this.player) === null || _a === void 0 ? void 0 : _a.mute();
    }
    /**
     * Unmutes video.
     *
     * @returns {void}
     */
    unMute() {
        var _a;
        return (_a = this.player) === null || _a === void 0 ? void 0 : _a.unMute();
    }
    /**
     * Disposes of the current video player.
     *
     * @returns {void}
     */
    dispose() {
        if (this.player) {
            this.player.destroy();
            this.player = null;
        }
    }
    /**
     * Cleans up the YouTube player and removes event listeners.
     *
     * @returns {void}
     */
    dispose() {
        var _a, _b, _c, _d, _e, _f;
        if (this.player) {
            try {
                if (this._onVolumeChangeBound) {
                    (_b = (_a = this.player).removeEventListener) === null || _b === void 0 ? void 0 : _b.call(_a, 'onVolumeChange', this._onVolumeChangeBound);
                    this._onVolumeChangeBound = null;
                }
                if (this.throttledFireUpdateSharedVideoEvent) {
                    (_d = (_c = this.player).removeEventListener) === null || _d === void 0 ? void 0 : _d.call(_c, 'onVideoProgress', this.throttledFireUpdateSharedVideoEvent);
                }
                (_f = (_e = this.player).destroy) === null || _f === void 0 ? void 0 : _f.call(_e);
            }
            catch (error) {
                logger.warn('Error destroying YouTube player:', error);
            }
            this.player = undefined;
        }
    }
    /**
     * Component unmount lifecycle method.
     *
     * @inheritdoc
     */
    componentWillUnmount() {
        this.dispose();
        super.componentWillUnmount();
    }
    /**
     * Implements React Component's render.
     *
     * @inheritdoc
     */
    render() {
        return (React.createElement(YouTube, Object.assign({}, this.getPlayerOptions())));
    }
}
export default connect(_mapStateToProps, _mapDispatchToProps)(YoutubeVideoManager);
